import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, ChefHat, CheckCircle, Package, ChevronRight } from 'lucide-react'
import { motion } from 'framer-motion'
import { useMenuTheme } from '@/contexts/MenuThemeContext'

interface OrderItem {
  productName?: string
  name?: string
  quantity: number
  product?: {
    name?: string
  }
}

interface ActiveOrderCardProps {
  order: {
    id: string
    status: string
    total: number
    items: OrderItem[]
    estimatedPreparationTime?: number
    preparationStartedAt?: string
    readyAt?: string
    createdAt: string
  }
}

const getStatusConfig = (status: string, primaryColor: string) => {
  switch (status) {
    case 'PREPARING':
      return {
        label: 'In Preparazione',
        icon: ChefHat,
        color: '#f97316',
        emoji: '👨‍🍳'
      }
    case 'READY':
      return {
        label: 'Pronto!',
        icon: CheckCircle,
        color: '#22c55e',
        emoji: '✅'
      }
    case 'SUBMITTED':
    default:
      return {
        label: 'Inviato',
        icon: Package,
        color: primaryColor,
        emoji: '📦'
      }
  }
}

export const ActiveOrderCard: React.FC<ActiveOrderCardProps> = ({ order }) => {
  const navigate = useNavigate()
  const { theme } = useMenuTheme()
  const config = getStatusConfig(order.status, theme.primaryColor)
  const StatusIcon = config.icon

  const calculateRemainingTime = (): number | null => {
    if (!order.estimatedPreparationTime) return null
    if (order.readyAt) return 0
    if (!order.preparationStartedAt) return order.estimatedPreparationTime

    const startTime = new Date(order.preparationStartedAt).getTime()
    const now = Date.now()
    const elapsedMinutes = Math.floor((now - startTime) / (1000 * 60))
    const remaining = order.estimatedPreparationTime - elapsedMinutes

    return Math.max(0, remaining)
  }

  const remainingTime = calculateRemainingTime()
  const itemsCount = order.items.reduce((sum, item) => sum + item.quantity, 0)

  // Get product name - handle different field structures from backend
  const getItemName = (item: OrderItem) =>
    item.productName || item.product?.name || item.name || 'Prodotto'

  // Format items list
  const itemsText = order.items
    .slice(0, 3)
    .map(item => `${item.quantity}x ${getItemName(item)}`)
    .join(', ')
  const hasMoreItems = order.items.length > 3

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => navigate(`/order-tracking/${order.id}`)}
      className="rounded-2xl p-4 cursor-pointer transition-all overflow-hidden"
      style={{
        backgroundColor: theme.cardBackground,
        borderColor: theme.borderColor,
        borderWidth: '1px',
      }}
    >
      {/* Header Row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {/* Status Icon */}
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${config.color}20` }}
          >
            <StatusIcon className="w-5 h-5" style={{ color: config.color }} />
          </div>

          {/* Status Info */}
          <div>
            <p className="font-semibold" style={{ color: config.color }}>
              {config.label}
            </p>
            <p className="text-xs" style={{ color: theme.mutedColor }}>
              {itemsCount} {itemsCount === 1 ? 'articolo' : 'articoli'}
            </p>
          </div>
        </div>

        <ChevronRight className="w-5 h-5" style={{ color: theme.mutedColor }} />
      </div>

      {/* Items Preview */}
      <p className="text-sm mb-3 line-clamp-1" style={{ color: theme.textSecondary }}>
        {itemsText}{hasMoreItems && ` +${order.items.length - 3} altri`}
      </p>

      {/* Time Badge - Only for preparing */}
      {order.status === 'PREPARING' && remainingTime !== null && remainingTime > 0 && (
        <div
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg mb-3"
          style={{ backgroundColor: `${config.color}15` }}
        >
          <Clock className="w-3.5 h-3.5" style={{ color: config.color }} />
          <span className="text-xs font-medium" style={{ color: config.color }}>
            ~{remainingTime} min
          </span>
        </div>
      )}

      {/* Ready Banner */}
      {order.status === 'READY' && (
        <div
          className="rounded-lg px-3 py-2 mb-3 text-center"
          style={{ backgroundColor: `${config.color}15` }}
        >
          <p className="text-sm font-semibold" style={{ color: config.color }}>
            🎉 Pronto per il ritiro!
          </p>
        </div>
      )}

      {/* Footer */}
      <div
        className="flex items-center justify-between pt-3"
        style={{ borderTopColor: theme.borderColor, borderTopWidth: '1px' }}
      >
        <span className="text-sm" style={{ color: theme.mutedColor }}>Totale</span>
        <span className="font-bold" style={{ color: theme.primaryColor }}>
          €{order.total.toFixed(2)}
        </span>
      </div>

      {/* CTA Hint */}
      <p className="text-xs text-center mt-3" style={{ color: theme.mutedColor }}>
        Tocca per vedere i dettagli →
      </p>
    </motion.div>
  )
}
